'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import PostGrid from './PostGrid';
import { Post } from '@/lib/posts';
import { fetchPosts } from '@/app/actions';

interface InfinitePostListProps {
  initialPosts: Post[];
}

export default function InfinitePostList({ initialPosts }: InfinitePostListProps) {
  const [posts, setPosts] = useState<Post[]>(initialPosts);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const observerRef = useRef<HTMLDivElement>(null);

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;
    setLoading(true);
    
    try {
      const nextPage = page + 1;
      const newPosts = await fetchPosts(nextPage, 18);
      
      if (newPosts.length === 0) {
        setHasMore(false);
      } else {
        setPosts(prev => [...prev, ...newPosts]);
        setPage(nextPage);
      }
    } catch (error) {
      console.error('Failed to load posts:', error);
    } finally {
      setLoading(false);
    }
  }, [page, loading, hasMore]);

  useEffect(() => {
    const element = observerRef.current;
    if (!element) return;

    const observer = new IntersectionObserver((entries) => {
      // If the sentinel is visible, load more
      if (entries[0].isIntersecting && !loading && hasMore) {
        loadMore();
      }
    }, { 
      threshold: 0.1,
      // Load when the sentinel is within 200px of the viewport aka "near bottom"
      rootMargin: '200px'
    });

    observer.observe(element);

    return () => {
      if (element) observer.unobserve(element);
    };
  }, [loadMore, loading, hasMore]);

  return (
    <>
      <PostGrid posts={posts} />
      
      <div
        ref={observerRef}
        className="w-full flex justify-center items-center py-20 min-h-[100px]"
      >
        {loading && (
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin h-8 w-8 border border-[#151515] border-t-transparent"></div>
            <span className="text-xs text-[#151515] font-light tracking-[0.15em] uppercase opacity-50">Loading</span>
          </div>
        )}
        {!hasMore && posts.length > 0 && (
          <div className="flex items-center gap-6 w-full justify-center">
            <div className="h-px bg-[#E2E2E2] w-32"></div>
            <span className="text-xs text-[#151515] font-light tracking-[0.15em] uppercase opacity-40">End</span>
            <div className="h-px bg-[#E2E2E2] w-32"></div>
          </div>
        )}
      </div>
    </>
  );
}
